import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import { adminJson } from '../utils/adminAuth';

const RANGES = [['today', 'Today'], ['7d', '7 days'], ['30d', '30 days'], ['lifetime', 'Lifetime']];
const fmtBytes = (b) => {
  b = Number(b || 0);
  if (b >= 1e9) return `${(b / 1e9).toFixed(1)} GB`;
  if (b >= 1e6) return `${(b / 1e6).toFixed(1)} MB`;
  if (b >= 1e3) return `${(b / 1e3).toFixed(0)} KB`;
  return `${b} B`;
};
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : '—');

export default function AdminMobile() {
  const navigate = useNavigate();
  const [view, setView] = useState('overview');
  const [range, setRange] = useState('lifetime');
  const [summary, setSummary] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('adminAuth');
    if (!token) { navigate('/admin/login'); return null; }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const load = useCallback(() => {
    const h = authHeader();
    if (!h) return;
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE}/admin/mobile/summary`, { headers: h }).then(adminJson),
      fetch(`${API_BASE}/admin/mobile/dealers?range=${range}`, { headers: h }).then(adminJson),
      fetch(`${API_BASE}/admin/mobile/timeline?range=${range}`, { headers: h }).then(adminJson),
      fetch(`${API_BASE}/admin/mobile/issues`, { headers: h }).then(adminJson),
    ]).then(([s, d, t, i]) => {
      setSummary(s);
      setDealers(d.dealers || []);
      setTimeline(t.timeline || []);
      setIssues(i.issues || (Array.isArray(i) ? i : []));
    }).catch(() => setError('Failed to load mobile activity')).finally(() => setLoading(false));
  }, [authHeader, range]);

  useEffect(() => { load(); }, [load]);

  const card = (label, value, sub) => (
    <div style={statCard}>
      <div style={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>{value}</div>
      <div style={{ fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      {sub && <div style={{ fontSize: 12, color: '#0078D4', marginTop: 2 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ padding: 20, maxWidth: 1150, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Mobile Activity</h2>
      </div>
      {error && <div style={{ color: '#dc2626', marginBottom: 8 }}>{error}</div>}

      {/* Summary cards (lifetime totals + today) */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 12, margin: '12px 0' }}>
          {card('Photos', summary.total_photos ?? 0, `${summary.today_photos ?? 0} today`)}
          {card('Videos', summary.total_videos ?? 0, `${summary.today_videos ?? 0} today`)}
          {card('Vehicles shot', summary.vehicles_photographed ?? 0, `${summary.today_vehicles ?? 0} today`)}
          {card('Completed', `${summary.completed ?? 0}/${summary.total_tracked ?? 0}`)}
          {card('Active dealers', summary.active_dealers ?? 0)}
          {card('Storage', fmtBytes(summary.total_storage_bytes))}
        </div>
      )}

      {/* View tabs + range */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '12px 0', flexWrap: 'wrap' }}>
        {['overview', 'timeline', 'issues'].map(v => (
          <button key={v} onClick={() => setView(v)} style={view === v ? tabActive : tab}>
            {v === 'overview' ? 'By dealer' : v === 'timeline' ? 'Timeline' : `Issues (${issues.length})`}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: '#6b7280' }}>Range:</span>
        <select value={range} onChange={e => setRange(e.target.value)} style={ctrl}>
          {RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {loading ? <div>Loading…</div> : (
        <>
          {view === 'overview' && (
            <table style={table}>
              <thead><tr>{['Dealer', 'Vehicles', 'Photos', 'Videos', 'Complete', 'Inventory', 'Storage', 'Last upload'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {dealers.map((d, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #eef2f7' }}>
                    <td style={td}>{d.dealer_name}{d.city ? <span style={{ color: '#94a3b8', fontSize: 12 }}> · {d.city}, {d.state}</span> : ''}</td>
                    <td style={td}>{d.vehicles}</td>
                    <td style={td}>{d.photos}</td>
                    <td style={td}>{d.videos}</td>
                    <td style={td}>{d.completed_vehicles}/{d.total_inventory}</td>
                    <td style={td}>{d.total_inventory}</td>
                    <td style={td}>{fmtBytes(d.storage_bytes)}</td>
                    <td style={td}>{fmtDate(d.last_upload)}</td>
                  </tr>
                ))}
                {!dealers.length && <tr><td colSpan={8} style={{ ...td, color: '#6b7280' }}>No activity in this range.</td></tr>}
              </tbody>
            </table>
          )}

          {view === 'timeline' && (
            <table style={table}>
              <thead><tr>{['Date', 'Photos', 'Videos', 'Vehicles', 'Dealers'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {timeline.map((t, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #eef2f7' }}>
                    <td style={td}>{fmtDate(t.upload_date)}</td>
                    <td style={td}>{t.photos}</td>
                    <td style={td}>{t.videos}</td>
                    <td style={td}>{t.vehicles}</td>
                    <td style={td}>{t.dealers}</td>
                  </tr>
                ))}
                {!timeline.length && <tr><td colSpan={5} style={{ ...td, color: '#6b7280' }}>No uploads in this range.</td></tr>}
              </tbody>
            </table>
          )}

          {view === 'issues' && (
            <div>
              {issues.map((it, i) => (
                <div key={i} style={{ border: '1px solid #e5e7eb', borderLeft: `4px solid ${it.severity === 'error' ? '#dc2626' : '#d97706'}`, borderRadius: 6, padding: '10px 12px', marginBottom: 8 }}>
                  <div style={{ fontSize: 12, color: it.severity === 'error' ? '#dc2626' : '#d97706', textTransform: 'uppercase' }}>{it.type} · {it.severity}</div>
                  <div>{it.message}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{it.dealer}</div>
                </div>
              ))}
              {!issues.length && <div style={{ color: '#059669', padding: 12 }}>✓ No data-integrity issues.</div>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const ctrl = { padding: 8, border: '1px solid #d1d5db', borderRadius: 6 };
const tab = { padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' };
const tabActive = { ...tab, background: '#0078D4', color: '#fff', border: 'none' };
const linkBtn = { background: 'none', border: 'none', color: '#0078D4', cursor: 'pointer', padding: 0 };
const statCard = { border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', background: '#fff' };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 14 };
const th = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' };
const td = { padding: '8px 10px', verticalAlign: 'top' };
